import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { createSeededRandom } from '../utils/random';
import {
  CHIP_DEFS, MINERAL_TIME_CAP, DYNAMITE_PENALTY, HOOK_HIT_RADIUS, CREATURE_DEFS,
  MINERAL_DEFS,
  PLAYER_COLORS,
  WORLD,
  levelCurve,
  type ChipId,
  type GameMode,
  type GameStateName,
  type MineralKind,
} from './config';
import { Hook } from './hook';
import { CreatureManager } from './creatures';
import { MagicEffects } from './magicEffects';
import { Replenishment } from './replenishment';
import type {MatchOptions} from './match';
import {
  disposeMineral,
  generateLevel, rawTreasureValue,
  type Mineral,
} from './minerals';
import { ChipLoadout, type ChipSlotName } from './chips';
import { stageForLevel } from './stages';
import { disposePixelAssets, pixelAssetsStatus, createToolSprite } from './pixelAssets';
import { BackdropManager } from './backdrops';
import { ComboTracker } from './combo';
import { WorldEventManager, type WorldEventType } from './worldEvents';
import { AudioSystem } from '../systems/AudioSystem';

export interface PlayerRuntime {
  id: number;
  hook: Hook;
  chips: ChipLoadout;
  combo: ComboTracker;
  money: number;
  score: number;
  hooksFired: number;
  lastGrabLabel: string;
  color: string;
  effects: { harvest: number; lantern: number; shield: number; survey: number; reel: number };
  magnetTime:number;
  blastCharge:boolean;
  round:{treasures:number;obstacles:number;empty:number;heavy:number;tools:number;pickups:number};
  starCharge: boolean;
  starlightCharge: boolean;
}

export type { GameMode, GameStateName };

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private readonly loop: Loop;
  private readonly audio = new AudioSystem();

  private rng = createSeededRandom(1);
  private minerals: Mineral[] = [];
  private players: PlayerRuntime[] = [];
  private readonly creatures = new CreatureManager(this.scene);
  private readonly magic = new MagicEffects(this.scene);
  private readonly replenishment = new Replenishment();
  private match:MatchOptions|null=null;
  private roundDuration=0;
  private initialPotential = 0;
  private mineralTimeUsed = 0;
  private events!: WorldEventManager;
  private readonly backdrop = new BackdropManager();
  private readonly shade = new THREE.Mesh(new THREE.PlaneGeometry(120, 120),
    new THREE.MeshBasicMaterial({ color: '#25392f', transparent: true, opacity: 0, depthWrite: false, depthTest: false }));
  private lights: { ambient: THREE.AmbientLight; sun: THREE.DirectionalLight; fill: THREE.PointLight } | null = null;

  private mode: GameMode = 'solo';
  private state: GameStateName = 'menu';
  private level = 1;
  private targetScore = 800;
  private timeLeft = 75;
  private elapsed = 0;
  private frame = 0;
  private complete = false;
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private shake = 0;
  private flash = 0;
  private timeScale = 1;
  private effectTimers = {
    magnet: 0,
    blast: 0,
    twin: 0,
    slow: 0,
    gravity: 0,
    fever: 0,
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    for(const id of ['rescueBell','mossShield','twinHook','starElixir'] as const){const sprite=createToolSprite(id,1);sprite.material.dispose();}
    this.renderer = createRenderer(canvas);
    this.camera = new THREE.OrthographicCamera(-16, 16, 12.5, -12.5, 0.1, 100);
    this.camera.position.set(0, -1.2, WORLD.cameraZ);
    this.camera.lookAt(0, -1.2, 0);
    this.loop = new Loop(
      (d, e) => {
        if (this.state === 'active-play' && !this.visualsReady) {
          this.frame += 1;
          resizeRenderer(this.renderer, this.camera);
          this.publishDiagnostics();
          return;
        }
        this.update(d, e);
      },
      () => this.render(),
    );
    this.buildScene();
    this.resetPlayers('solo');
    this.installTestHooks();
    resizeRenderer(this.renderer, this.camera, 2);
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.audio.dispose();
    this.events.dispose();
    this.creatures.dispose();
    this.magic.dispose();
    this.shade.geometry.dispose();
    this.shade.material.dispose();
    for (const p of this.players) p.hook.dispose();
    this.backdrop.dispose();
    this.clearMinerals();
    this.renderer.dispose();
    disposePixelAssets();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  // —— public API for UI ——

  setMode(mode: GameMode): void {
    this.mode = mode;
    this.resetPlayers(mode);
    this.state = mode === 'solo' ? 'menu' : 'two-player';
    this.publishDiagnostics();
  }

  private foundGold = false;
  private totalHooks = 0;
  private discovered = new Set<MineralKind>();
  private discoveryListener: ((kind: MineralKind) => void) | null = null;
  setAccountCounters(hooks: number, collection: readonly string[], onDiscover: (kind: MineralKind) => void): void {
    this.totalHooks = Math.max(0, Math.floor(hooks));
    this.discovered = new Set(collection as MineralKind[]);
    this.discoveryListener = onDiscover;
  }
  playersStats(): { hooks: number; combo: number; foundGold: boolean } {
    return { foundGold: this.foundGold, hooks: this.totalHooks, combo: Math.max(...this.players.map((p) => p.combo.multiplier)) };
  }

  getMode(): GameMode {
    return this.mode;
  }

  getState(): GameStateName {
    return this.state;
  }

  get visualsReady(): boolean { return this.backdrop.status === 'ready' && pixelAssetsStatus() === 'ready'; }
  get visualLoadError(): boolean { return this.backdrop.status === 'error' || pixelAssetsStatus() === 'error'; }

  configureMatch(options:MatchOptions|null):void {this.match=options?{...options,duration:options.duration===0?0:Number.isFinite(options.duration)?Math.max(30,Math.min(600,Math.round(options.duration))):90,loadouts:options.loadouts}:null;}

  startLevel(level: number, chipPrimary: ChipId = 'magnet', chipSecondary: ChipId = 'blast'): void {
    this.level = Number.isFinite(level) ? Math.max(1, Math.min(10, Math.floor(level))) : 1;
    level = this.level;
    this.rng = createSeededRandom(level * 997 + (this.mode === 'coop' ? 11 : this.mode === 'versus' ? 22 : 0));
    this.creatures.clear();
    this.magic.clear();
    this.clearMinerals();
    this.minerals = generateLevel(level, this.rng, this.scene, this.mode !== 'solo');
    this.initialPotential=rawTreasureValue(this.minerals);this.mineralTimeUsed=0;
    this.replenishment.reset(this.minerals.length);
    this.creatures.reset(level,this.rng,this.minerals,this.mode!=='solo');
    this.events.bindMinerals(this.minerals);
    this.events.reset(this.rng, stageForLevel(level).events,this.players.map(p=>p.hook.owner.pivotX));
    this.backdrop.setStage(stageForLevel(level));
    this.shade.material.opacity = 0;
    this.events.schedule(true);

    const curve = levelCurve(level);
    this.targetScore = this.mode === 'coop' ? Math.round(curve.target * 2) : curve.target;
    this.timeLeft = this.mode === 'versus' ? this.match?.duration||90 : this.mode==='coop'&&this.match?.duration?this.match.duration:curve.time;
    this.roundDuration=this.timeLeft;
    this.elapsed = 0;
    this.complete = false;
    this.state = 'active-play';
    this.timeScale = 1;
    this.resetEffectTimers();

    this.players.forEach((p, i) => {
      const px = this.players.length > 1 ? (i === 0 ? -4.2 : 4.2) : 0;
      p.hook.reset(px);
      p.hook.setDifficulty(curve.swingMultiplier);
      p.hook.setSurfaceRatio(stageForLevel(level).surfaceRatio);
      p.money = 0;
      p.score = 0;
      p.hooksFired = 0;
      p.combo.reset();
      const equipped=this.mode!=='solo'?this.match?.loadouts[i]:undefined;
      p.chips = new ChipLoadout(equipped?.[0]??chipPrimary,equipped?.[1]??chipSecondary, stageForLevel(level).fieldTool);
      p.effects = { harvest:0, lantern:0, shield:0, survey:0, reel:0 };
      p.starCharge = false; p.starlightCharge = false;
      p.magnetTime=0;p.blastCharge=false;p.round={treasures:0,obstacles:0,empty:0,heavy:0,tools:0,pickups:0};
    });

    this.refreshHud();
    this.publishDiagnostics();
  }

  fire(playerId: number): void {
    const p = this.players[playerId];
    if (!p || this.state !== 'active-play') return;
    if (p.hook.state !== 'swinging') return;
    p.hook.fire();
    p.hooksFired += 1;
    this.totalHooks += 1;
    this.audio.fire();
  }

  activateChip(playerId: number, slot: ChipSlotName): ChipId | null {
    const p = this.players[playerId];
    if (!p || this.state !== 'active-play') return null;
    if(p.chips[slot].id==='ropeShears'&&p.hook.state!=='extending'&&p.hook.state!=='retracting')return null;
    const id = p.chips.activate(slot);
    if (!id) return null;
    this.applyChip(playerId, id);
    p.round.tools++;
    this.audio.chip();
    return id;
  }

  pause(): void {
    if (this.state === 'active-play') this.state = 'paused';
    else if (this.state === 'paused') this.state = 'active-play';
    this.publishDiagnostics();
  }

  returnToMenu(): void {
    this.state = 'menu';
    this.magic.clear();
    this.publishDiagnostics();
  }

  setReducedMotion(enabled: boolean): void { this.reducedMotion = enabled; }

  isPaused(): boolean {
    return this.state === 'paused';
  }

  getPlayerHud(index: number): {
    money: number;
    target: number;
    timeLeft: number;
    combo: string;
    chipP: string;
    chipS: string;
    chipPCooldown: number;
    chipSCooldown: number;
    chipF: string; chipFCooldown: number;
    chipPId:ChipId;chipSId:ChipId;chipFId:ChipId;activeTools:ChipId[];
    chipPAvailable: boolean; chipSAvailable: boolean; chipFAvailable: boolean;
    effects: string; prediction: string;
  } | null {
    const p = this.players[index];
    if (!p) return null;
    return {
      money: p.money,
      target: this.targetScore,
      timeLeft: Math.max(0, this.timeLeft),
      combo: p.combo.label,
      chipP: CHIP_DEFS[p.chips.primary.id].name,
      chipS: CHIP_DEFS[p.chips.secondary.id].name,
      chipPCooldown: p.chips.primary.cooldownLeft,
      chipSCooldown: p.chips.secondary.cooldownLeft,
      chipF: CHIP_DEFS[p.chips.field.id].name, chipFCooldown: p.chips.field.cooldownLeft,
      chipPId:p.chips.primary.id,chipSId:p.chips.secondary.id,chipFId:p.chips.field.id,
      activeTools:[p.magnetTime>0?'magnet':null,p.blastCharge?'blast':null,p.effects.reel>0?'twinHook':null,p.effects.shield>0?'mossShield':null,p.effects.harvest>0?'seedPouch':null,p.effects.lantern>0?'lantern':null,p.effects.survey>0?'surveyLens':null,p.starCharge?'starElixir':null,this.effectTimers.gravity>0?'gravityFlip':null,this.effectTimers.slow>0?'timeDilation':null,p.combo.fever?'goldFever':null].filter((id):id is ChipId=>id!==null),
      chipPAvailable: p.chips.primary.usesLeft > 0, chipSAvailable: p.chips.secondary.usesLeft > 0, chipFAvailable: p.chips.field.usesLeft > 0,
      effects: [p.effects.reel>0?`绞盘 ${Math.ceil(p.effects.reel)}s`:'',this.effectTimers.gravity>0?`羽毛 ${Math.ceil(this.effectTimers.gravity)}s`:'',this.effectTimers.slow>0?`慢时 ${Math.ceil(this.effectTimers.slow)}s`:'',p.effects.harvest > 0 ? `丰收 ${Math.ceil(p.effects.harvest)}s` : '', p.effects.lantern > 0 ? `提灯 ${Math.ceil(p.effects.lantern)}s` : '', p.effects.shield > 0 ? `护盾 ${Math.ceil(p.effects.shield)}s` : '', p.effects.survey > 0 ? `探脉 ${Math.ceil(p.effects.survey)}s` : '', p.starCharge ? '星愿待用' : '', p.starlightCharge ? '星辉待用' : ''].filter(Boolean).join(' · '),
      prediction: p.effects.survey > 0 ? this.predictTarget(p) : '',

    };
  }

  getSummary(): {
    state: GameStateName;
    mode: GameMode;
    level: number;
    scores: number[];
    target: number;
    timeLeft: number;
    event: string;
    stageId: string; background: string; backgroundStatus: string;
    potential:number;remainingPotential:number;threat:string;mineralTimeUsed:number;
    rule:'timed'|'race';winner:number|null;roundDuration:number;refill:string;spawned:number;roundStats:PlayerRuntime['round'][];vfxCount:number;
  } {
    return {
      state: this.state,
      mode: this.mode,
      level: this.level,
      scores: this.players.map((p) => p.money),
      target: this.targetScore,
      timeLeft: Math.max(0, this.timeLeft),
      event: this.events.current.type,
      stageId: stageForLevel(this.level).id, background: this.backdrop.path, backgroundStatus: this.backdrop.status,
      potential:this.initialPotential,remainingPotential:rawTreasureValue(this.minerals.filter(m=>m.active)),threat:this.creatures.message,mineralTimeUsed:this.mineralTimeUsed,
      rule:this.match?.rule??'timed',winner:this.players.length===2?(this.players[0].money===this.players[1].money?null:this.players[0].money>this.players[1].money?0:1):null,
      roundDuration:this.roundDuration,refill:this.replenishment.message,spawned:this.replenishment.spawned,roundStats:this.players.map(p=>({...p.round})),vfxCount:this.magic.count,
    };
  }

  // —— internals ——

  private resetPlayers(mode: GameMode): void {
    for (const p of this.players) {
      this.scene.remove(p.hook.group);
      p.hook.dispose();
    }
    this.players = [];
    this.foundGold = false;
    const count = mode === 'solo' ? 1 : 2;
    for (let i = 0; i < count; i++) {
      const id = i;
      const pivotX = count === 1 ? 0 : i === 0 ? -4.2 : 4.2;
      const hook = new Hook({ id, pivotX, color: PLAYER_COLORS[i] });
      this.scene.add(hook.group);
      this.players.push({
        id,
        hook,
        chips: new ChipLoadout('magnet', 'blast'),
        combo: new ComboTracker(),
        money: 0,
        score: 0,
        hooksFired: 0,
        lastGrabLabel: '',
        color: PLAYER_COLORS[i],
        magnetTime:0,blastCharge:false,round:{treasures:0,obstacles:0,empty:0,heavy:0,tools:0,pickups:0},
        effects: { harvest:0, lantern:0, shield:0, survey:0, reel:0 }, starCharge:false, starlightCharge:false,
      });
    }
  }

  private buildScene(): void {
    this.scene.background = null;
    this.scene.add(this.backdrop.mesh);
    this.backdrop.setStage(stageForLevel(1));
    this.shade.position.z = 2;
    this.shade.renderOrder = 10;
    this.scene.add(this.shade);
    const ambient = new THREE.AmbientLight('#fff0d4', 1.4);
    const sun = new THREE.DirectionalLight('#ffe3ac', 1.5);
    sun.position.set(-6, 10, 12);
    const fill = new THREE.PointLight('#a6c8a0', 0.6, 40);
    fill.position.set(6, -2, 6);
    this.scene.add(ambient, sun, fill);
    this.lights = { ambient, sun, fill };
    this.events = new WorldEventManager(this.scene, this.rng, (t) => this.onWorldEvent(t));
  }

  private clearMinerals(): void {
    for (const m of this.minerals) disposeMineral(m, this.scene);
    this.minerals = [];
  }

  private resetEffectTimers(): void {
    this.effectTimers = { magnet: 0, blast: 0, twin: 0, slow: 0, gravity: 0, fever: 0 };
    this.timeScale = 1;
  }

  private applyChip(playerId: number, id: ChipId): void {
    const p = this.players[playerId];
    switch (id) {
      case 'magnet':
        p.magnetTime=CHIP_DEFS.magnet.duration;
        p.hook.setMagnet(true, CHIP_DEFS.magnet.duration);
        break;
      case 'blast':
        this.effectTimers.blast = 30;
        p.blastCharge=true;
        p.hook.setBlast(true);
        break;
      case 'twinHook':
        p.effects.reel = CHIP_DEFS.twinHook.duration;
        p.hook.setTwin(true);

        break;
      case 'timeDilation':
        this.effectTimers.slow = CHIP_DEFS.timeDilation.duration;
        this.timeScale = 0.35;
        break;
      case 'gravityFlip':
        this.effectTimers.gravity = CHIP_DEFS.gravityFlip.duration;
        for (const pl of this.players) pl.hook.setGravityFlip(true);
        break;
      case 'pocketClock': if(this.mode==='versus'){p.money+=120;p.score+=120;}else this.timeLeft += 6; break;
      case 'seedPouch': p.effects.harvest = CHIP_DEFS.seedPouch.duration; break;
      case 'lantern': p.effects.lantern = CHIP_DEFS.lantern.duration; this.events.lightUp(); break;
      case 'mossShield': p.effects.shield = CHIP_DEFS.mossShield.duration; break;
      case 'surveyLens': p.effects.survey = CHIP_DEFS.surveyLens.duration; break;
      case 'starElixir': p.starCharge = true; break;
      case 'rescueBell': this.creatures.scareAll(CHIP_DEFS.rescueBell.duration); break;
      case 'ropeShears': p.hook.cutRope(); break;
      case 'goldFever':
        this.effectTimers.fever = 8;
        p.combo.enableFever();
        break;
    }
    this.magic.activate(id,playerId,{x:p.hook.owner.pivotX,y:WORLD.pivotY+.8},()=>({x:p.hook.tip.x,y:p.hook.tip.y}));
    this.refreshHud();
  }

  private onWorldEvent(type: WorldEventType): void {
    this.flash = type === 'blackout' ? 0.2 : 0.35;
    this.audio.event(type);
    this.refreshHud();
  }

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    resizeRenderer(this.renderer, this.camera);
    if (this.pausedForScreenshot || this.state !== 'active-play') {
      this.publishDiagnostics();
      return;
    }

    if (this.state === 'active-play' && !this.complete) {
      this.elapsed += delta;
      this.timeLeft -= delta;
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.state = this.mode==='versus'?'level-clear':'game-over';
        this.complete=this.mode==='versus';
        if(this.mode==='versus')this.audio.win();else this.audio.fail();this.refreshHud();this.publishDiagnostics();return;
      }
    }

    const animDelta = delta;
    this.magic.update(delta,this.reducedMotion);
    if(this.mode==='versus'){
      const added=this.replenishment.update(delta,this.match?.refill??'steady',this.level,this.rng,this.minerals,this.scene);
      if(added.length){this.minerals=this.minerals.filter(m=>m.active);this.minerals.push(...added);this.events.bindMinerals(this.minerals);for(const m of added)this.magic.reward(m.x,m.y);}
    }
    this.creatures.update(delta,this.timeScale,this.minerals,this.reducedMotion);
    this.events.update(animDelta * this.timeScale, this.minerals, this.players.map((p) => ({ x: p.hook.tip.x, y: p.hook.tip.y })));

    // Effects timers
    if (this.effectTimers.slow > 0) {
      this.effectTimers.slow -= delta;
      if (this.effectTimers.slow <= 0) this.timeScale = 1;
    }
    if (this.effectTimers.gravity > 0) {
      this.effectTimers.gravity -= delta;
      if (this.effectTimers.gravity <= 0) {
        for (const p of this.players) p.hook.setGravityFlip(false);
      }
    }

    for (const p of this.players) {
      p.magnetTime=Math.max(0,p.magnetTime-delta);p.hook.setMagnet(p.magnetTime>0,p.magnetTime);
      for (const effect of Object.keys(p.effects) as (keyof PlayerRuntime['effects'])[]) p.effects[effect] = Math.max(0, p.effects[effect] - delta);
      p.hook.setTwin(p.effects.reel > 0);
      p.chips.update(delta);
      p.combo.update(delta);
      if(p.magnetTime<=0)this.magic.stop('magnet',p.id);
      if(p.effects.shield<=0)this.magic.stop('mossShield',p.id);
      if(!p.combo.fever)this.magic.stop('goldFever',p.id);
      if (this.state === 'active-play') {
        const before=p.hook.state,wasHolding=p.hook.grabbedMineral!==null;
        p.hook.update(
          animDelta,
          this.timeScale,
          this.minerals,
          (m) => this.onGrab(p, m),
          (m) => this.onDeposit(p, m),
          (x, y) => this.onBlast(p, x, y),
        );
        if(before==='retracting'&&!wasHolding&&p.hook.state==='swinging')p.round.empty++;
        if(p.hook.state==='extending'){
          const hit=this.creatures.hit(p.hook.tip.x,p.hook.tip.y,p.effects.shield>0);
          if(hit&&p.effects.shield<=0){p.hook.interrupt(hit.delay);p.combo.onGrab('none');p.lastGrabLabel=CREATURE_DEFS[hit.kind].name;this.audio.grab('rock');}
        }
      }
    }

    // ambient mineral bob
    if (!this.reducedMotion) {
      for (const m of this.minerals) {
        if (!m.active || m.grabbedBy !== null) continue;
        if (m.bobPhase < -1) continue;

        m.group.position.y = m.y + Math.sin(elapsed * 1.4 + m.bobPhase) * 0.05;
      }
    }

    // blackout light
    if (this.lights) {
      const b = this.players.some((p) => p.effects.lantern > 0) ? 0 : this.events.blackoutAmount;
      this.shade.material.opacity = b * 0.55;
      this.lights.ambient.intensity = 0.55 * (1 - b * 0.85);
      this.lights.sun.intensity = 1.35 * (1 - b * 0.9);
      this.lights.fill.intensity = 0.85 + b * 0.4;
    }

    if (this.shake > 0) this.shake = Math.max(0, this.shake - delta * 2.5);
    if (this.flash > 0) this.flash = Math.max(0, this.flash - delta * 1.8);

    // camera shake
    const sx = this.shake * 0.35 * Math.sin(elapsed * 40);
    const sy = this.shake * 0.25 * Math.cos(elapsed * 37);
    this.camera.position.x = sx;
    this.camera.position.y = -1.2 + sy;

    if (this.state === 'active-play') this.checkWin();
    this.refreshHud();
    this.publishDiagnostics();
  }

  private onGrab(p: PlayerRuntime, m: Mineral): void {
    if(m.pickup){p.lastGrabLabel=`魔法补给 · ${CHIP_DEFS[m.pickup].name}`;m.group.userData.multiplier=1;return;}
    this.discover(m.kind);
    p.lastGrabLabel = MINERAL_DEFS[m.kind].label;
    if (m.kind === 'dynamite') {
      p.round.obstacles++;this.magic.impact(m.x,m.y);
      if (p.effects.shield <= 0) p.money = Math.max(0, p.money - DYNAMITE_PENALTY);
      p.combo.onGrab('none');
      this.shake = this.reducedMotion ? 0 : 0.3;
      this.flash = 0.8;
      this.audio.blast();
      this.clearGrabbed(m);
      // retract force
      p.hook.state = 'retracting';
      return;
    }

    const comboResult = p.combo.onGrab(m.combo as never);
    const bonus = m.combo !== 'none';
    if(bonus)p.round.treasures++;else p.round.obstacles++;
    if(m.weight>=6)p.round.heavy++;
    m.group.userData.multiplier = comboResult.multiplier
      * (p.effects.harvest > 0 && m.combo === 'gold' ? 1.4 : 1)
      * (p.starCharge && bonus ? 2 : 1) * (p.starlightCharge && bonus ? 1.2 : 1);
    if (bonus) { p.starCharge = false; p.starlightCharge = false;this.magic.stop('starElixir',p.id); }
    const def = MINERAL_DEFS[m.kind];
    p.lastGrabLabel = def.label;
    this.audio.grab(m.kind);
    if (comboResult.broke) this.flash = 0.25;
  }

  private onDeposit(p: PlayerRuntime, m: Mineral): void {
    if(m.pickup){p.round.pickups++;this.applyChip(p.id,m.pickup);this.audio.chip();this.clearGrabbed(m);return;}
    const def = MINERAL_DEFS[m.kind];
    const gain = Math.round(m.value * (m.group.userData.multiplier ?? 1));
    if (m.kind.startsWith('gold')) this.foundGold = true;
    p.money += gain;
    p.score += gain;
    if (def.effect === 'time') {if(this.mode==='versus'){const bonus=(def.effectAmount??0)*20;p.money+=bonus;p.score+=bonus;}else{const bonus=Math.min(def.effectAmount??0,MINERAL_TIME_CAP-this.mineralTimeUsed);this.timeLeft+=bonus;this.mineralTimeUsed+=bonus;}}
    if (def.effect === 'combo') p.combo.timer += def.effectAmount ?? 0;
    if (def.effect === 'shield') p.effects.shield = Math.max(p.effects.shield, def.effectAmount ?? 0);
    if (def.effect === 'starlight') p.starlightCharge = true;
    this.audio.deposit(gain);
    this.magic.reward(p.hook.owner.pivotX,WORLD.pivotY+.7);
    this.clearGrabbed(m);
    this.checkLevelTargets();
  }

  private onBlast(p: PlayerRuntime, x: number, y: number): void {
    p.blastCharge=false;this.magic.stop('blast',p.id);this.magic.impact(x,y);
    this.shake = this.reducedMotion ? 0 : 0.3;
    this.flash = 0.9;
    this.audio.blast();
    this.creatures.scareNear(x,y,3);
    // remove nearby rocks
    for (const m of this.minerals) {
      if (!m.active || m.grabbedBy !== null) continue;
      const d = Math.hypot(m.x - x, m.y - y);
      if (m.kind === 'rock' && d < 2.8) {
        this.clearGrabbed(m);
      } else if (d < 2.2 && m.weight < 2) {
        // scatter
        const nx=m.x+(m.x-x)*.35,ny=m.y+(m.y-y)*.35;
        const reachable=this.players.some(p=>Math.abs(Math.atan2(nx-p.hook.owner.pivotX,WORLD.pivotY-ny))<.88&&Math.hypot(nx-p.hook.owner.pivotX,WORLD.pivotY-ny)<23);
        const clear=this.minerals.every(other=>other===m||!other.active||Math.hypot(nx-other.x,ny-other.y)>=m.radius+other.radius+.2);
        if(reachable&&clear){m.x=nx;m.y=ny;m.group.position.set(m.x,m.y,0);}
      }
    }
  }

  private discover(kind: MineralKind): void {
    if (this.discovered.has(kind)) return;
    this.discovered.add(kind);
    this.discoveryListener?.(kind);
  }

  private predictTarget(p: PlayerRuntime): string {
    const sx = Math.sin(p.hook.directionAngle), sy = -Math.cos(p.hook.directionAngle);
    const hit = this.minerals.filter((m) => m.active && m.hiddenBy===null && m.grabbedBy === null)
      .map((m) => ({ m, distance: (m.x - p.hook.owner.pivotX) * sx + (m.y - WORLD.pivotY) * sy,
        side: Math.abs((m.x - p.hook.owner.pivotX) * sy - (m.y - WORLD.pivotY) * sx) }))
      .filter((v) => v.distance > 1.2 && v.distance < WORLD.maxHookLength && v.side < v.m.radius + HOOK_HIT_RADIUS)
      .sort((a,b) => a.distance-b.distance)[0];
    return hit ? `${MINERAL_DEFS[hit.m.kind].label} · ${hit.m.value} 金币` : '前方暂未发现矿物';
  }

  private clearGrabbed(m: Mineral): void {
    m.active = false;
    m.grabbedBy = null;
    m.group.visible = false;
    disposeMineral(m, this.scene);
  }

  private checkLevelTargets(): void {
    if (this.mode === 'versus') {
      if((this.match?.rule??'timed')!=='race')return;
      for (const p of this.players) {
        if (p.money >= this.targetScore && !this.complete) {
          this.complete = true;
          this.state = 'level-clear';
          this.audio.win();
        }
      }
      return;
    }
    const total = this.players.reduce((s, p) => s + p.money, 0);
    if (total >= this.targetScore && !this.complete) {
      this.complete = true;
      this.state = 'level-clear';
      this.audio.win();
    }
  }

  private checkWin(): void {
    if (this.complete) return;
    if (this.mode === 'versus') {
      if((this.match?.rule??'timed')!=='race')return;
      const leader = this.players.reduce((a, b) => (a.money >= b.money ? a : b));
      if (leader.money >= this.targetScore) {
        this.complete = true;
        this.state = 'level-clear';
        this.audio.win();
      }
    }
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private refreshHud(): void {
    // UI reads getSummary/getPlayerHud each frame via rAF; keep light.
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (name === 'active-play' || name === 'combo' || name === 'coop' || name === 'versus') {
          if (name === 'coop') this.setMode('coop');
          else if (name === 'versus') this.setMode('versus');
          else this.setMode('solo');
          this.startLevel(this.level || 1);
          if (name === 'combo') {
            this.players[0].combo.streak = 5;
            this.players[0].combo.multiplier = 3;
            this.players[0].combo.color = 'gold';
          }
          this.render();
          this.publishDiagnostics();
          return { state: name };
        }
        if (name === 'level-clear') {
          this.startLevel(this.level || 1);
          this.players.forEach((p) => (p.money = this.targetScore));
          this.checkLevelTargets();
          this.render();
          this.publishDiagnostics();
          return { state: name };
        }
        if (name === 'game-over') {
          this.startLevel(this.level || 1);
          this.timeLeft = 0;
          this.state = 'game-over';
          this.render();
          this.publishDiagnostics();
          return { state: name };
        }
        if (name === 'menu' || name === 'two-player' || name === 'chip-select' || name === 'paused' || name === 'auth') {
          this.state = name === 'auth' ? 'menu' : name;
          this.render();
          this.publishDiagnostics();
          return { state: name };
        }
        throw new Error(`Unknown test state: ${name}`);
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.render();
        this.publishDiagnostics();
      },
      hideDebugUi: (_hidden: boolean) => {
        void _hidden;
      },
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: this.players[0]?.money ?? 0,
      targetScore: this.targetScore,
      complete: this.complete,
      state: this.state,
      mode: this.mode,
      stageId: stageForLevel(this.level).id, background: this.backdrop.path, backgroundStatus: this.backdrop.status,
      player: {
        position: {
          x: this.players[0]?.hook.tip.x ?? 0,
          y: this.players[0]?.hook.tip.y ?? 0,
          z: 0,
        },
        speed: 0,
      },
      players: this.players.map((p) => ({ id: p.id, money: p.money, combo: p.combo.multiplier })),
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
      },
    };
  }
}

export type { MineralKind };
